// src/pages/PatientBookAppointmentPage.jsx
// Route: /patient/appointments/book
// Patient selects a doctor, date and available slot then confirms booking.
//
// Rebuilt onto the shared design system to match the navigator portal's own
// BookAppointmentPage (src/pages/BookAppointmentPage.js), which is the
// reference for this screen. Previously this page hand-rolled every control —
// its own `inputStyle` object, its own label styling, its own error box (with
// hardcoded hex), its own pill-shaped slot buttons and its own submit button —
// on a bare canvas with no form sections, so it read as a different product
// from the navigator booking the same appointment.
//
// The three UX gaps that came with that, beyond the styling:
//   1. The confirm button sat greyed out with nothing saying why. A patient who
//      hadn't picked a slot yet just saw a dead button. The navigator page
//      solves this with a line under the button naming the missing step, which
//      this now mirrors.
//   2. The subtitle promised "a convenient time slot" but no slot UI existed
//      until a doctor was chosen — the page looked like it had no time
//      selection at all. The date/time panel is now always visible and says
//      what to do next.
//   3. A raw <input type="date"> made the 7-day booking window invisible until
//      you hit the min/max. The calendar greys out everything outside it.
//
// Payment flow (Razorpay / wallet), the 7-day window and the flat ₹250 fee are
// unchanged — this is a presentation change, not a behaviour change.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PatientAppShell } from '../components/PatientLayout';
import { usePatient } from '../context/PatientContext';
import { doctorAPI, appointmentAPI, walletAPI } from '../services/api';
import { payAndCreateAppointment, payAndCreateAppointmentWithWallet } from '../utils/appointmentPayment';
import Icon from '../components/Icon';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

// Patients may book up to a week ahead — a product rule this page has always
// had. It used to live only in the date input's max attribute plus a caption;
// the calendar now shows it directly by disabling out-of-range days.
const BOOKING_WINDOW_DAYS = 7;

// Display only. AppointmentPricingService resolves the real charge server-side;
// this page never sends a price.
const CONSULTATION_FEE_RUPEES = 250;

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function isoFor(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-IN', {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });
}

// The slots endpoint has returned both plain "HH:mm" strings and
// { startTime, formattedTime, available } objects depending on the caller.
// Normalise once so the rest of the page doesn't branch on shape: `value` is
// what gets submitted, `label` is what the patient reads.
function normaliseSlots(payload) {
  const raw = Array.isArray(payload) ? payload
    : Array.isArray(payload?.slots) ? payload.slots
    : Array.isArray(payload?.availableSlots) ? payload.availableSlots
    : [];
  return raw.map((slot, i) => (
    typeof slot === 'string'
      ? { key: slot, label: slot, value: slot, available: true }
      : {
          key: slot.startTime ?? String(i),
          label: slot.formattedTime ?? slot.startTime ?? String(i),
          value: slot.startTime ?? slot.formattedTime,
          available: slot.available !== false,
        }
  ));
}

export default function PatientBookAppointmentPage() {
  const { patient } = usePatient();
  const navigate    = useNavigate();

  const [doctors, setDoctors]     = useState([]);
  const [slots, setSlots]         = useState([]);
  const [loadingDocs, setLoadingDocs]   = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [success, setSuccess]           = useState(false);
  const [error, setError]               = useState('');

  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [apptType, setApptType]             = useState('CONSULTATION');
  const [walletBalanceMinorUnits, setWalletBalanceMinorUnits] = useState(0);

  // Calendar state — mirrors the navigator page's calendar rather than a raw
  // date input, so both portals pick a date the same way.
  const today = startOfToday();
  const [calYear, setCalYear]   = useState(today.getFullYear());
  const [calMonth, setCalMonth] = useState(today.getMonth());
  const [selDay, setSelDay]     = useState(today.getDate());
  const [selectedSlot, setSelectedSlot] = useState(null);

  const selectedDate = selDay ? isoFor(calYear, calMonth, selDay) : '';

  const lastBookableDate = new Date(today);
  lastBookableDate.setDate(lastBookableDate.getDate() + BOOKING_WINDOW_DAYS);

  // 27 Jul 2026: new — powers the "Pay with wallet" option below.
  useEffect(() => {
    if (!patient?.id) return;
    walletAPI.getBalance(patient.id)
      .then(res => setWalletBalanceMinorUnits(res.data.balanceMinorUnits || 0))
      .catch(() => { /* silent — wallet option just won't show if this fails */ });
  }, [patient?.id]);

  // Load doctors
  useEffect(() => {
    doctorAPI.list()
      .then(res => setDoctors((res.data ?? []).filter(d => d.isAvailable)))
      .catch(() => setError('Could not load doctors.'))
      .finally(() => setLoadingDocs(false));
  }, []);

  // Load slots when doctor + date change
  useEffect(() => {
    if (!selectedDoctor || !selectedDate) { setSlots([]); return; }
    setLoadingSlots(true);
    setSelectedSlot(null);
    appointmentAPI.availableSlots(selectedDoctor, selectedDate)
      .then(res => setSlots(normaliseSlots(res.data)))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDoctor, selectedDate]);

  async function handleBook(usingWallet) {
    if (!selectedDoctor || !selectedDate || !selectedSlot) {
      setError('Please select a doctor, date and time slot.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      // 16 Jul 2026: was calling appointmentAPI.create() directly with no
      // payment step at all — same gap PatientOrderLabTestPage.jsx had
      // before it was fixed. Now gated on a verified Razorpay payment
      // first — see src/utils/appointmentPayment.js. Flat ₹250 today
      // (AppointmentPricingService resolves the actual amount server-side;
      // this page never trusts a client-side number for the real charge).
      const doctor = doctors.find(d => d.id === selectedDoctor);
      const doctorName = doctor ? `Dr. ${doctor.firstName || ''} ${doctor.lastName || ''}`.trim() : undefined;

      const appointmentPayload = {
        patientId:       patient.id,
        doctorId:        selectedDoctor,
        appointmentDate: selectedDate,
        appointmentTime: selectedSlot.value,
        appointmentType: apptType,
      };

      // 27 Jul 2026: routes to the wallet-combined checkout when the patient
      // picked "Pay with wallet" — same two functions used by the chat
      // ordering flow (src/utils/appointmentPayment.js), not a separate
      // implementation.
      const payFn = usingWallet ? payAndCreateAppointmentWithWallet : payAndCreateAppointment;
      const result = await payFn({
        patientId:    patient.id,
        doctorId:     selectedDoctor,
        appointmentType: apptType,
        doctorName,
        patientName:  `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
        patientEmail: patient.email,
        patientPhone: patient.phone,
        appointmentPayload,
      });

      if (result.success) {
        setSuccess(true);
        return;
      }

      // Distinct wording per failure point, same reasoning as
      // PatientOrderLabTestPage.jsx — "click to retry" only makes sense
      // for a cancelled/failed payment attempt, not for the
      // appointment-creation-after-payment case, which needs support
      // contact instead of a naive retry (retrying could mean paying twice).
      if (result.reason === 'payment_cancelled') {
        setError('Payment was not completed. Click "Pay & Confirm Appointment" to try again.');
      } else if (result.reason === 'payment_failed') {
        setError(`Payment failed: ${result.message || 'please try again.'}`);
      } else if (result.reason === 'appointment_creation_failed') {
        setError(result.message);
      } else {
        setError(result.message || 'Could not book appointment. Please try again.');
      }
    } catch (e) {
      setError(e?.response?.data?.message ?? 'Could not book appointment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <PatientAppShell breadcrumbs={[
        { label: 'My Appointments', to: '/patient/appointments' },
        { label: 'Booking Confirmed' },
      ]}>
        <div className="empty-state" style={{ paddingTop: 60 }}>
          <div className="empty-icon empty-icon--badge" aria-hidden="true">
            <Icon name="check" size={22} />
          </div>
          <div className="empty-title">Appointment confirmed</div>
          <div className="empty-desc">
            Your appointment on <strong>{formatDate(selectedDate)}</strong> at{' '}
            <strong>{selectedSlot?.label}</strong> has been booked.
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginTop: 24 }}>
            <button className="btn btn-primary"
              onClick={() => navigate('/patient/appointments')}>
              View appointments
            </button>
            <button className="btn btn-ghost"
              onClick={() => { setSuccess(false); setSelectedSlot(null); setSelectedDoctor(''); }}>
              Book another
            </button>
          </div>
        </div>
      </PatientAppShell>
    );
  }

  // ── Calendar maths ──────────────────────────────────────────────────────
  const firstDay       = new Date(calYear, calMonth, 1).getDay();
  const daysInMonth    = new Date(calYear, calMonth + 1, 0).getDate();
  const isCurrentMonth = calYear === today.getFullYear() && calMonth === today.getMonth();

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear(y => y - 1); setCalMonth(11); }
    else setCalMonth(m => m - 1);
    setSelDay(null); setSelectedSlot(null);
  };
  const nextMonth = () => {
    if (calMonth === 11) { setCalYear(y => y + 1); setCalMonth(0); }
    else setCalMonth(m => m + 1);
    setSelDay(null); setSelectedSlot(null);
  };

  // What the patient still has to do, in the order the form asks for it —
  // shown under the confirm button so a disabled button always says why.
  const missingStep = !selectedDoctor ? 'Select a doctor'
    : !selectedDate ? 'Select a date'
    : !selectedSlot ? 'Select a time slot'
    : null;

  const canSubmit = !missingStep && !submitting;

  return (
    <PatientAppShell breadcrumbs={[
      { label: 'My Appointments', to: '/patient/appointments' },
      { label: 'Book Appointment' },
    ]}>
      {/* <button>, not the <span> the navigator page uses — same styling, but
          reachable by keyboard and announced as a control. */}
      <button className="back-link fade-in-up" onClick={() => navigate('/patient/appointments')}>
        ← Back
      </button>

      <div className="page-header fade-in-up">
        <div>
          <div className="page-title">Book an Appointment</div>
          <div className="page-subtitle">Choose a doctor and a convenient time slot.</div>
        </div>
      </div>

      {error && <div className="alert alert-error">⚠️ {error}</div>}

      {/* .book-appt-grid overrides .form-split's default two-independent-
          columns shape with named grid-areas, so "2. Select Date & Time"
          can sit between "1. Doctor & Type" and "3. Confirm & Pay" once the
          grid collapses to one column on mobile. Previously Doctor & Type
          and Confirm & Pay were nested together in one column-1 <div>, with
          Select Date & Time as a separate column-2 <div> — visually fine
          side by side on desktop, but once .form-split's own mobile media
          query drops to a single column, the two divs simply stack in DOM
          order: column 1 in full (Doctor & Type, THEN Confirm & Pay), then
          column 2 (Select Date & Time) — landing the payment section
          before the patient had even picked a time. Named areas place each
          section by name instead of by which wrapper div it happened to be
          nested in, so desktop keeps its two-column look while mobile
          stacks them in the order a patient actually needs.

          .book-appt-grid .btn is the same page-scoped pill-radius override
          as PatientAppointmentsPage.jsx's .appt-page .btn — see that
          comment for why it's scoped rather than touching the shared .btn
          class everywhere. */}
      <style>{`
        .book-appt-grid { grid-template-areas: "doctor datetime" "confirm datetime"; }
        .book-appt-doctor   { grid-area: doctor; }
        .book-appt-datetime { grid-area: datetime; }
        .book-appt-confirm  { grid-area: confirm; }
        .book-appt-grid .btn { border-radius: 999px; }
        @media (max-width: 768px) {
          .book-appt-grid { grid-template-areas: "doctor" "datetime" "confirm"; }
        }
      `}</style>

      <div className="form-split book-appt-grid">

        <div className="form-section fade-in-up delay-1 book-appt-doctor">
          <div className="form-section-title">1. Doctor &amp; Type</div>
          <div className="form-grid" style={{ gridTemplateColumns: '1fr' }}>
            <div className="form-group">
              <label className="form-label" htmlFor="book-doctor">
                Doctor <span className="required">*</span>
              </label>
              {loadingDocs ? (
                <div className="loading-state" style={{ padding: '10px 0', justifyContent: 'flex-start' }}>
                  <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                  Loading doctors…
                </div>
              ) : (
                <select
                  id="book-doctor"
                  className="form-select"
                  value={selectedDoctor}
                  onChange={e => { setSelectedDoctor(e.target.value); setError(''); }}
                >
                  <option value="">— Choose a doctor —</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>
                      Dr. {d.firstName} {d.lastName}
                      {d.specialty ? ` — ${d.specialty}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="book-type">
                Appointment Type <span className="required">*</span>
              </label>
              <select
                id="book-type"
                className="form-select"
                value={apptType}
                onChange={e => setApptType(e.target.value)}
              >
                <option value="CONSULTATION">Consultation</option>
                <option value="FOLLOW_UP">Follow Up</option>
                <option value="REVIEW">Review</option>
              </select>
            </div>
          </div>
        </div>

        {/* ── Calendar + slots — see the .book-appt-grid comment above for
            why this now sits between the doctor/type and confirm/pay
            sections in the JSX, rather than after both. ── */}
        <div className="form-section fade-in-up delay-2 book-appt-datetime" style={{ alignSelf: 'start' }}>
          <div className="form-section-title">2. Select Date &amp; Time</div>

          <div className="calendar-header">
            <button type="button" className="btn btn-ghost btn-sm" onClick={prevMonth}
              aria-label="Previous month">‹</button>
            <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
              {MONTHS[calMonth]} {calYear}
            </span>
            <button type="button" className="btn btn-ghost btn-sm" onClick={nextMonth}
              aria-label="Next month">›</button>
          </div>

          <div className="calendar-grid" style={{ marginBottom: 4 }}>
            {DAYS.map(d => <div key={d} className="cal-day header">{d}</div>)}
            {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} className="cal-day" />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const date       = new Date(calYear, calMonth, day);
              const isPast     = date < today;
              const isBeyond   = date > lastBookableDate;
              const isToday    = isCurrentMonth && day === today.getDate();
              const isSelected = selDay === day;
              const disabled   = isPast || isBeyond;
              return (
                <div key={day}
                  className={`cal-day ${disabled ? 'past' : isSelected ? 'selected' : isToday ? 'today' : 'available'}`}
                  onClick={() => { if (!disabled) { setSelDay(day); setSelectedSlot(null); setError(''); } }}
                >
                  {day}
                </div>
              );
            })}
          </div>

          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            Bookings available up to {BOOKING_WINDOW_DAYS} days from today
          </div>

          {/* The slot panel is always present, rather than appearing only once a
              doctor is picked — otherwise the page shows no time selection at
              all and the subtitle's promise of "a convenient time slot" has
              nothing behind it. */}
          <div style={{ marginTop: 18 }}>
            {!selectedDoctor ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                ☝ Choose a doctor to see available slots
              </div>
            ) : !selDay ? (
              <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                ☝ Select a date to see available slots
              </div>
            ) : (
              <>
                <div className="form-label" style={{ marginBottom: 10 }}>
                  Available slots — {selDay} {MONTHS[calMonth].slice(0, 3)}
                </div>
                {loadingSlots ? (
                  <div className="loading-state" style={{ padding: '12px 0', justifyContent: 'flex-start' }}>
                    <div className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                    Loading slots…
                  </div>
                ) : slots.length === 0 ? (
                  <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '12px 0' }}>
                    No slots available for this date. Try another date.
                  </div>
                ) : (
                  <>
                    <div className="slot-grid">
                      {slots.map(slot => {
                        const isSelSlot = selectedSlot?.key === slot.key;
                        return (
                          <div key={slot.key}
                            className={`slot ${!slot.available ? 'taken' : isSelSlot ? 'selected' : 'available'}`}
                            onClick={() => { if (slot.available) { setSelectedSlot(slot); setError(''); } }}
                          >
                            {slot.label}{isSelSlot ? ' ✓' : ''}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', gap: 14, fontSize: 12, color: 'var(--text-muted)' }}>
                      <span>🟦 Available</span>
                      <span style={{ opacity: 0.5 }}>⬜ Booked</span>
                      {/* --blue, not --brand-blue — brand-blue is a fixed
                          navy that never flips in dark theme, which read as
                          near-invisible against this row's dark-theme text
                          color. Same fix as .slot.available in index.css. */}
                      <span style={{ color: 'var(--blue)' }}>✓ Selected</span>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>

        <div className="form-section fade-in-up delay-3 book-appt-confirm">
          <div className="form-section-title">3. Confirm &amp; Pay</div>

            {/* Consultation fee — flat ₹250 today, matches
                AppointmentPricingService's current behaviour; the server always
                resolves the actual charge independently, this is display-only. */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 14px', background: 'var(--bg-subtle)',
              borderRadius: 'var(--radius-md)', border: '1px solid var(--border)',
              fontSize: 13, marginBottom: 16,
            }}>
              <span style={{ color: 'var(--text-secondary)' }}>Consultation fee</span>
              <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                ₹{CONSULTATION_FEE_RUPEES}
              </span>
            </div>

            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-primary btn-lg"
                onClick={() => handleBook(false)}
                disabled={!canSubmit}
              >
                {submitting ? 'Processing…' : 'Pay & Confirm Appointment'}
              </button>
              <button type="button" className="btn btn-ghost"
                onClick={() => navigate('/patient/appointments')}>
                Cancel
              </button>
            </div>

            {/* A disabled button with no explanation was the main complaint
                this screen invited — same treatment as the navigator page. */}
            {missingStep && (
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                ℹ {missingStep} to confirm
              </div>
            )}

            {/* 27 Jul 2026: wallet payment option, only shown once there's an
                actual balance to offer. Mirrors the chat card's approach. */}
            {walletBalanceMinorUnits > 0 && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginTop: 12 }}
                onClick={() => handleBook(true)}
                disabled={!canSubmit}
              >
                👛 Pay with wallet (₹{(walletBalanceMinorUnits / 100).toLocaleString('en-IN')} available)
              </button>
            )}
          </div>
      </div>
    </PatientAppShell>
  );
}
